import { patchStakeholder } from '@sonar-software/sonar-core';
import { PlaintextCellEditor } from '@ui/text';
import { SonarInputRule } from '@lib/validation';
import { ErrorOrFailStatusCode, SuccessStatusCode } from '@sonar-software/sonar-http';
import {
	genericProcessingFailure,
	ProcessingResult,
	useProcessing,
	useStakeholderReference,
	useUpdateStakeholderReference
} from '@hooks';
import { NOTIFICATIONS_COPY, STAKEHOLDER_NAME } from '@sonar-software/sonar-ts-constants';
import React, { useCallback } from 'react';

const rules: Array<SonarInputRule> = [
	{
		max: STAKEHOLDER_NAME.MAX_LENGTH,
		message: STAKEHOLDER_NAME.REQUIRED_MESSAGE,
		min: STAKEHOLDER_NAME.MIN_LENGTH,
		pattern: STAKEHOLDER_NAME.PATTERN,
		required: true
	}
];

async function updateName(stakeholderId: number, name: string): Promise<ProcessingResult> {
	const result = await patchStakeholder({
		name,
		stakeholderId
	});

	if (result.status === ErrorOrFailStatusCode.Conflict) {
		return {
			message: NOTIFICATIONS_COPY.STAKEHOLDER_CONFLICT,
			success: false
		};
	}

	if (result.status !== SuccessStatusCode.NoContent) {
		return genericProcessingFailure;
	}

	return { success: true };
}

export interface StakeholderNameCellEditorProps {
	stakeholderId: number;
}

export const StakeholderNameCellEditor: React.FC<StakeholderNameCellEditorProps> = ({ stakeholderId }) => {
	const { name } = useStakeholderReference(stakeholderId);
	const { reset, start, state, stop } = useProcessing();
	const update = useUpdateStakeholderReference();

	const handleSave = useCallback(
		async(newValue: string) => {
			if (name === newValue) {
				return;
			}

			start();
			const updateResult = await updateName(stakeholderId, newValue);

			if (updateResult.success) {
				update({
					name: newValue,
					stakeholderId
				});
			}

			stop(updateResult);
		},
		[ name, start, stakeholderId, stop, update ]
	);

	return (
		<PlaintextCellEditor
			id={ `stakeholder-name-${stakeholderId}` }
			isProcessing={ state.isProcessing }
			maxLength={ STAKEHOLDER_NAME.MAX_LENGTH }
			onSave={ handleSave }
			onStartEditing={ reset }
			processingResult={ state.result }
			rules={ rules }
			skipSpellcheck
			value={ name } />
	);
};
